import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface SeoConfiguration {
  id: string;
  page_path: string;
  page_name: string;
  title?: string;
  description?: string;
  image_url?: string;
  canonical_url?: string;
  keywords?: string;
  og_type?: string;
  og_title?: string;
  og_url?: string;
  og_image?: string;
  og_image_alt?: string;
  og_description?: string;
  og_site_name?: string;
  author?: string;
  copyright?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useSeoConfigurations = () => {
  const [seoConfigurations, setSeoConfigurations] = useState<SeoConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchSeoConfigurations = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('seo_configurations')
        .select('*')
        .order('page_path', { ascending: true });

      if (error) throw error;
      setSeoConfigurations(data || []);
    } catch (error) {
      console.error('Error fetching SEO configurations:', error);
      toast({
        title: "Error",
        description: "Failed to fetch SEO configurations",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createSeoConfiguration = async (seoData: Omit<SeoConfiguration, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('seo_configurations')
        .insert([seoData])
        .select()
        .single();

      if (error) throw error;
      
      setSeoConfigurations(prev => [...prev, data]);
      toast({
        title: "Success",
        description: "SEO configuration created successfully",
      });
      return data;
    } catch (error) {
      console.error('Error creating SEO configuration:', error);
      toast({
        title: "Error",
        description: "Failed to create SEO configuration",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateSeoConfiguration = async (id: string, updates: Partial<SeoConfiguration>) => {
    try {
      const { data, error } = await supabase
        .from('seo_configurations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setSeoConfigurations(prev => prev.map(config => config.id === id ? data : config));
      toast({
        title: "Success",
        description: "SEO configuration updated successfully",
      });
      return data;
    } catch (error) {
      console.error('Error updating SEO configuration:', error);
      toast({
        title: "Error",
        description: "Failed to update SEO configuration",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteSeoConfiguration = async (id: string) => {
    try {
      const { error } = await supabase
        .from('seo_configurations')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setSeoConfigurations(prev => prev.filter(config => config.id !== id));
      toast({
        title: "Success",
        description: "SEO configuration deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting SEO configuration:', error);
      toast({
        title: "Error",
        description: "Failed to delete SEO configuration",
        variant: "destructive",
      });
      throw error;
    }
  };

  useEffect(() => {
    fetchSeoConfigurations();
  }, []);

  return {
    seoConfigurations,
    loading,
    createSeoConfiguration,
    updateSeoConfiguration,
    deleteSeoConfiguration,
    refreshSeoConfigurations: fetchSeoConfigurations,
  };
};